import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, LayoutGrid, List } from "lucide-react";
import Skeleton from "components/ui/Skeleton";
import NoDataState from "modules/swiftboard/components/NoDataState";
import ChapterCard from "./knowledge-base-hub/ChapterCard";
import ArticleRow from "./knowledge-base-hub/ArticleRow";
import FilterPills from "./knowledge-base-hub/FilterPills";
import ProgressCard from "./knowledge-base-hub/ProgressCard";
import UpNextCard from "./knowledge-base-hub/UpNextCard";
import EmptyKnowledgeBase from "./knowledge-base-hub/EmptyKnowledgeBase";
import useKnowledgeHub from "./knowledge-base-hub/useKnowledgeHub";
import { FILTERS, isUnread } from "./knowledge-base-hub/knowledge-filters";

const RECENT_LIMIT = 4;

const HubSkeleton = () => (
  <div className="space-y-4">
    <Skeleton className="h-20 w-full" rounded="rounded-lg" />
    <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-36 w-full" rounded="rounded-lg" />
      ))}
    </div>
  </div>
);

const SectionTitle = ({ children, count, action }) => (
  <div className="flex items-center justify-between gap-3">
    <h2 className="text-base font-semibold text-text_primary">
      {children}
      {count !== undefined && <span className="ml-1.5 text-sm font-medium text-text_muted">{count}</span>}
    </h2>
    {action}
  </div>
);

const KnowledgeBase = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [filter, setFilter] = useState("all");
  const [layout, setLayout] = useState("grid");

  useEffect(() => {
    const handle = setTimeout(() => setSearchText(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { loading, chapters, articles, toggleBookmark } = useKnowledgeHub(searchText);

  const openArticle = (id) => navigate(`/swiftboard/knowledge-base/${id}`);
  const openChapter = (id) => navigate(`/swiftboard/knowledge-base/chapter/${id}`);

  const readCount = articles.filter((a) => !isUnread(a)).length;

  const upNext = useMemo(() => {
    const unread = articles.filter(isUnread);
    return unread.find((a) => a.viewer_state?.is_pinned) || unread[0];
  }, [articles]);

  const recent = useMemo(
    () =>
      [...articles]
        .sort((a, b) => new Date(b.published_at || 0) - new Date(a.published_at || 0))
        .slice(0, RECENT_LIMIT),
    [articles]
  );

  const filtering = filter !== "all" || !!searchText;
  const results = useMemo(() => articles.filter(FILTERS[filter].test), [articles, filter]);

  const renderBody = () => {
    if (loading) return <HubSkeleton />;

    if (articles.length === 0) {
      return searchText ? (
        <NoDataState
          icon="folder"
          title="No Articles Found"
          message={`No articles match "${searchText}".`}
          showRefresh={false}
          showBack={false}
          showCreate={false}
          size="medium"
        />
      ) : (
        <EmptyKnowledgeBase />
      );
    }

    if (filtering) {
      return (
        <section className="space-y-4">
          <SectionTitle>
            {searchText ? `Results for "${searchText}"` : FILTERS[filter].label} · {results.length}
          </SectionTitle>
          {results.length === 0 ? (
            <NoDataState
              icon="folder"
              title="Nothing here yet"
              message="No articles match this filter."
              showRefresh={false}
              showBack={false}
              showCreate={false}
              size="small"
            />
          ) : (
            <div className="rounded-lg border border-border bg-surface overflow-hidden">
              {results.map((a) => (
                <ArticleRow key={a.id} article={a} onOpen={openArticle} onToggleBookmark={toggleBookmark} />
              ))}
            </div>
          )}
        </section>
      );
    }

    return (
      <>
        {upNext && <UpNextCard article={upNext} onOpen={openArticle} />}

        <section className="space-y-4">
          <SectionTitle
            count={chapters.length}
            action={
              <div className="flex items-center gap-1.5">
                {[
                  ["grid", LayoutGrid, "Grid view"],
                  ["list", List, "List view"],
                ].map(([key, Icon, label]) => (
                  <button
                    key={key}
                    type="button"
                    aria-label={label}
                    onClick={() => setLayout(key)}
                    className={`p-2 rounded-md border cursor-pointer ${
                      layout === key
                        ? "bg-primary_tint text-primary border-primary/40"
                        : "bg-surface text-text_muted border-border"
                    }`}
                  >
                    <Icon size={14} />
                  </button>
                ))}
              </div>
            }
          >
            Chapters
          </SectionTitle>
          <div className={layout === "grid" ? "grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4" : "flex flex-col gap-3"}>
            {chapters.map((chapter, i) => (
              <ChapterCard key={chapter.id} chapter={chapter} number={i + 1} onOpen={openChapter} compact={layout === "list"} />
            ))}
          </div>
        </section>

        <section className="space-y-4">
          <SectionTitle>Recently added</SectionTitle>
          <div className="rounded-lg border border-border bg-surface overflow-hidden">
            {recent.map((a) => (
              <ArticleRow key={a.id} article={a} onOpen={openArticle} onToggleBookmark={toggleBookmark} />
            ))}
          </div>
        </section>
      </>
    );
  };

  return (
    <div className="h-full overflow-y-auto bg-surface_alt">
      <div className="max-w-[100rem] mx-auto w-full py-3 space-y-4">
        {/* Toolbar: search, filters and reading progress. No page heading —
            the breadcrumb and sidebar already name the page. */}
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-surface shadow-sm px-3 sm:px-4 py-3">
          <div className="relative w-full sm:w-72">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text_muted" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={`Search ${articles.length || ""} articles...`.replace("  ", " ")}
              className="w-full pl-8 pr-3 py-2 text-xs rounded-md border border-border bg-transparent text-text_primary placeholder:text-text_muted outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>

          {!loading && articles.length > 0 && (
            <div className="min-w-0 max-w-full">
              <FilterPills articles={articles} value={filter} onChange={setFilter} />
            </div>
          )}

          <div className="ml-auto">
            {loading ? (
              <Skeleton className="w-36 h-9" rounded="rounded-md" />
            ) : (
              articles.length > 0 && <ProgressCard read={readCount} total={articles.length} />
            )}
          </div>
        </div>

        {renderBody()}
      </div>
    </div>
  );
};

export default KnowledgeBase;
